import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { useNavigateToPlaygroundFromRun } from '@/Pages/Playground/hooks/useNavigateToPlaygroundFromRun';
import { CopyMultiButton } from '@/components/CopyButton/CopyMultiButton';
import { getRunTypeClassName } from '@/components/RunType.utils';
import { RunTypeIcon } from '@/components/RunTypeIcon';
import { Skeleton } from '@/components/Skeleton';
import { TruncatedTextWithTooltip } from '@/components/TruncatedTextWithTooltip.tsx';
import { getRunName } from '@/utils/get-run-name';
import { useResizeObserver } from '@/utils/use-resize-observer';

import { PageTitle } from '../../../components/PageTitle';
import { RunSchema } from '../../../types/schema';
import { RunActions } from '../RunActions';

export const RunHeader = (props: {
  run: RunSchema | undefined | null;
  isComparing?: boolean;
  priorThreadRunIds?: string[];
  onClose?: () => void;
}) => {
  const { datasetShareToken, shareToken } = useParams();

  const [actionCollapsedSize, setActionCollapsedSize] = useState<
    'full' | 'half' | 'closed'
  >('full');

  const containerRef = useResizeObserver<HTMLDivElement>((entry) => {
    const width = entry[0]?.contentRect.width;
    if (width && width < 700) {
      setActionCollapsedSize(width < 400 ? 'closed' : 'half');
    } else {
      setActionCollapsedSize('full');
    }
  });
  const onPlaygroundNavigate = useNavigateToPlaygroundFromRun(
    props.priorThreadRunIds
  );

  return (
    <div
      ref={containerRef}
      className="mb-3 flex flex-col items-start justify-between gap-2 px-4 lg:flex-row lg:items-center lg:gap-4"
    >
      <PageTitle
        className="py-0"
        icon={
          props.run ? (
            <RunTypeIcon run={props.run} />
          ) : (
            <Skeleton className="my-0 h-5 w-5" />
          )
        }
        iconClassName={props.run ? getRunTypeClassName(props.run) : ''}
      >
        {props.run ? (
          <div className="flex gap-2">
            <TruncatedTextWithTooltip text={getRunName(props.run)} />{' '}
            <CopyMultiButton
              copyItems={[
                { prefix: 'Trace ID', item: props.run.trace_id },
                { prefix: 'Run ID', item: props.run.id },
              ]}
              textSize="sm"
            />
          </div>
        ) : (
          <Skeleton className="h-[24px] w-[200px]" />
        )}
      </PageTitle>

      {props.run && (
        <div className="mr-1.5 flex items-center gap-4 whitespace-nowrap">
          <RunActions
            actionCollapsedSize={actionCollapsedSize}
            isComparing={props.isComparing}
            run={props.run}
            isPublicPage={Boolean(datasetShareToken) || Boolean(shareToken)}
            priorThreadRunIds={props.priorThreadRunIds}
            onPlaygroundNavigate={onPlaygroundNavigate}
            onClose={props.onClose}
          />
        </div>
      )}
    </div>
  );
};
